/**
 * Vehicle Custom Layer for FlowRide Frontend
 * Custom WebGL layer for rendering vehicles on MapLibre GL map.
 * Handles real-time vehicle visualization with textures, animations, and interactions.
 */

import maplibregl from "maplibre-gl";
import * as math from "mathjs";
import { animationOrchestrator } from "../../managers/AnimationOrchestrator";
import { type VehicleManager } from "../../managers/VehicleManager";
import { type Color } from "../../model/AtlasConfig";
import { type Vehicle } from "../../model/Vehicle";
import { VehicleType } from "../../model/VehicleType";
import type { VehicleLayerMemoryDiagnostics } from "../../tools/AppMemoryReport.types";
import * as colorTools from "../../tools/ColorTools";
import { timelineEpochTimeWindowMs } from "../../tools/compactTimelineDisplay.tools";
import {
    findNextStopIndexDelayAware,
    getDelayForStopSequence,
    getNormalizedArrivalSecondsWithDelay,
} from "../../tools/MapItineraryTools";
import { secondsToMinutes } from "../../tools/TimeRelated";
import { type ZoomFpsConfig } from "../../types/MapConfiguration";
import { type ICustomLayer } from "./ICustomLayer";
import { VEHICLE_CUSTOM_LAYER_ID } from "./mapLayerIds";
import {
    areVehicleSelectionLngLatEqual,
    isSamePopupContent,
} from "./vehicleSelection/vehicleSelection.tools";
import { type PopupContent, type RealtimeTransitPathSelection } from "./vehicleSelection/vehicleSelection.types";

/** Extra padding on the rendered icon quad so taps near the sprite edge still register. */
const VEHICLE_HIT_PADDING = 1.2;

/** Boats use a wide sprite; a bit more padding makes the ferry easier to tap. */
const VEHICLE_HIT_PADDING_BOAT = 1.45;

/** Callback that publishes the selected vehicle snapshot to React state. */
type UpdateVehicleSelection = ( lngLat: [number, number], content?: PopupContent ) => void;

/** Callback fired each frame with the selected vehicle render position (same timestamp as draw). */
type OnSelectedVehicleFramePosition = ( lngLat: [number, number] ) => void;

/** SVG texture family used for realtime vehicle sprites on the map. */
type VehicleTextureFamily = "bus" | "car" | "tram" | "boat";

/** Shader parameters for drawing route labels inside a vehicle paint zone. */
interface VehicleLetterLabelLayout {

    /** Glyph scale inside each character slot (1 = default; >1 magnifies, <1 shrinks). */
    "glyphScale": number;

    /** Top inset inside the paint zone (0–0.45). */
    "padTop": number;

    /** Bottom inset inside the paint zone (0–0.45). */
    "padBottom": number;

    /** Horizontal inset inside each character half-slot (0–0.45). */
    "padSide": number;

    /** Gap between two characters as a fraction of paint-zone width (0–0.4). */
    "charGap": number;

    /** When true, preserve square glyph aspect ratio inside non-square slots. */
    "preserveGlyphAspect": boolean;
}

/** Callback invoked when the selected vehicle changes (null = deselected). */
type OnVehicleSelectionChange = ( snapshot: RealtimeTransitPathSelection | null ) => void;

export class VehicleCustomLayer implements ICustomLayer {

    /** Layer identifier */
    id: string;


    /** Layer type */
    type: "custom";


    /** Rendering mode */
    renderingMode: "2d";


    /** MapLibre GL map instance */
    map!: maplibregl.Map;


    /** WebGL2 rendering context */
    gl!: WebGL2RenderingContext;


    /** Vehicle manager instance */
    vehicleManager: VehicleManager;


    /** Whether the layer is ready for rendering */
    ready: boolean = false;


    /** Currently selected vehicle (trip overlay + Dynamic Island content source). */
    clickedVehicle: Vehicle | undefined;

    /** Realtime content published to the Dynamic Island bridge. */
    popupContent?: PopupContent;

    /** Shader sources and programs */
    vertexShaderSource: string;

    fragmentShaderSource: string;

    program!: WebGLProgram;

    /** WebGL buffers for rendering */
    positionBuffer!: WebGLBuffer;

    texCoordBuffer!: WebGLBuffer;

    charCoordBuffer1!: WebGLBuffer;

    charCoordBuffer2!: WebGLBuffer;

    paintZoneBuffer!: WebGLBuffer;

    instanceCornerPositionBuffer!: WebGLBuffer;

    /** WebGL textures for different vehicle types */
    textureLetters!: WebGLTexture;

    private textureUnitVehicle: number = 2;

    /** Whether textures are fully loaded and ready for rendering */
    texturesLoaded: boolean = false;


    /** Texture unit for letters */
    textureUnitLetters: number = 5;

    private svgTemplateByType = new Map<VehicleTextureFamily, string>();

    private vehicleTextureCache = new Map<string, {
        "texture":    WebGLTexture;
        "lastUsedMs": number;
    }>();

    private vehicleTextureBuilds = new Map<string, Promise<void>>();

    private textureDescriptorByKey = new Map<string, {
        "effectiveType": VehicleTextureFamily;
        "color":         Color;
    }>();

    private vehicleTextureKeys: string[] = [];

    private renderBatches: Array<{
        "textureKey": string;
        "start":      number;
        "count":      number;
    }> = [];

    private vehicleTextureKeyById = new Map<string, string>();

    /** Channel quantization step used to reduce texture cache key cardinality. */
    private readonly vehicleColorQuantizationStep = 16;

    /** Minimum luminance applied to keep dark vehicle colors readable on map backgrounds. */
    private readonly minVehicleLuminance = 102;

    /** Maximum number of generated vehicle textures kept in the LRU cache (average texture size: 64kB). */
    private readonly maxVehicleTextureCacheSize = 1024;

    /** Square canvas size used to rasterize each recolored vehicle SVG. */
    private readonly vehicleTextureCanvasSize = 128;

    /** RGBA bytes per pixel used to estimate memory footprint of each vehicle texture. */
    private readonly vehicleTextureBytesPerPixel = 4;

    /**
     * Approximate texture memory per vehicle icon.
     * Formula: size * size * RGBA(4 bytes) => 128 * 128 * 4 = 65536 bytes (~64 KiB).
     */
    private readonly approximateVehicleTextureBytes = this.vehicleTextureCanvasSize * this.vehicleTextureCanvasSize * this.vehicleTextureBytesPerPixel;

    /**
     * Approximate max cache memory footprint (excluding WebGL driver overhead).
     * Formula: `maxVehicleTextureCacheSize * approximateVehicleTextureBytes`
     * Current value: 1024 * 65536 = 4’294’967’296 bytes (~64.0 MiB).
     */
    private readonly approximateVehicleTextureCacheBytes = this.maxVehicleTextureCacheSize * this.approximateVehicleTextureBytes;

    /**
     * Returns WebGL texture and instance-buffer diagnostics for memory profiling.
     *
     * @returns Estimated GPU-related cache footprint and buffer sizes.
     */
    public getMemoryDiagnostics(): VehicleLayerMemoryDiagnostics {
        const instanceCornerFloats = this.instanceCornerPositions?.length ?? 0;

        return {
            "renderedVehicleCount":       this.vehicles.length,
            "textureCacheEntries":        this.vehicleTextureCache.size,
            "textureCacheEstimatedBytes": this.vehicleTextureCache.size * this.approximateVehicleTextureBytes,
            "textureBuildsPending":       this.vehicleTextureBuilds.size,
            "maxInstanceBufferCapacity":  this.maxBufferSize,
            instanceCornerFloats,
            "instanceCornerBytes":        instanceCornerFloats * Float32Array.BYTES_PER_ELEMENT,
            "vehicleIndexMapEntries":     this.vehicleIndexMap.size,
        };
    }

    /** Inner scale factor to preserve margins when drawing SVGs into raster canvas. */
    private readonly vehicleTextureCanvasPaddingRatio = 0.9;

    /** Map icon size multiplier for ferry and watercraft (wide SVG, low height in square texture). */
    private readonly boatIconSizeMultiplier = 2;

    // Vehicle data
    vehicles: Vehicle[] = [];

    // Stable index tracking for incremental updates
    private vehicleIndexMap = new Map<string, number>();

    private nextFreeIndex: number = 0;

    private freeIndices: number[] = [];

    /** Flag to prevent updateVehiclePositions() from running during refreshData() */
    private isRefreshing: boolean = false;

    private maxBufferSize: number = 1000; // Initial buffer size

    instanceCornerPositions!: Float32Array;

    charTextureCoords1!: Float32Array;

    charTextureCoords2!: Float32Array;

    paintZones!: Float32Array;

    // Camera variables
    eye_high!: Float32Array;

    eye_low!: Float32Array;

    cameraMoved: boolean = true;

    updateVehicleSelection: UpdateVehicleSelection;

    /** Optional callback invoked when the selected vehicle changes. */
    onVehicleSelectionChange: OnVehicleSelectionChange | null = null;

    /**
     * Optional callback invoked after the selected vehicle position is resolved for the current frame.
     * Used to lock the camera on the same coordinates as the drawn sprite.
     */
    onSelectedVehicleFramePosition: OnSelectedVehicleFramePosition | null = null;

    /** Throttle noisy skip logs */
    private lastSkipLogMs: number = 0;

    /** Detailed skip log throttle */
    private lastSkipDetailLogMs: number = 0;

    /** Per-trip fallback log throttle to avoid console spam. */
    private fallbackLogLastMsByTrip: Map<string, number> = new Map();

    private readonly fallbackLogThrottleMs: number = 1_000;

    private static readonly SKIP_SAMPLE_LIMIT = 5;

    /** Minimum interval between position updates (ms) */
    private minPositionUpdateIntervalMs: number = 0;

    /** Last time vehicle positions were updated (ms) */
    private lastPositionUpdateMs: number = 0;

    /** FPS tracking for position updates */
    private positionUpdateFrameCount: number = 0;

    private lastBusFpsUpdateMs: number = 0;

    private currentBusFps: number = 0;

    /** Adaptive FPS configuration based on zoom level */
    private zoomFpsConfig: ZoomFpsConfig = null;

    /** Callback to report bus position update FPS */
    private onBusFpsUpdate: ( ( busFps: number ) => void ) | null = null;

    /** Last lng/lat forwarded to React to avoid per-frame setState churn. */
    private lastPublishedSelectionLngLat: [number, number] | null = null;

    /** Last popup content forwarded to React; reference equality when unchanged. */
    private lastPublishedSelectionContent: PopupContent | undefined;

    /** Whether the cleared-selection sentinel was already published. */
    private hasPublishedClearedSelection = false;

    private readonly handleContextLost = () => {
        // Prevent default behavior to allow restoration
        console.warn( "WebGL context lost for VehicleCustomLayer. Releasing resources." );
        this.ready = false;
        window.location.reload();

        // Optional: release resources here if necessary.
    };

    private readonly handleContextRestored = () => {
        // console.log( "WebGL context restored for VehicleCustomLayer. Reinitializing resources." );
        try {
            // Reinitialize resources: shaders, buffers, textures, etc.
            this.initShaders();
            this.initBuffers();
            this.loadTextures();
            this.ready = true;

            // Force a repaint of the map to redisplay the layer
            animationOrchestrator.requestRepaint();
        } catch( error ) {
            console.error( "Error during reinitialization after context restoration:", error );
        }
    };

    constructor( vehicleManager: VehicleManager, updateVehicleSelection: UpdateVehicleSelection ) {
        this.id = VEHICLE_CUSTOM_LAYER_ID;
        this.type = "custom";
        this.renderingMode = "2d";
        this.vehicleManager = vehicleManager;
        this.updateVehicleSelection = updateVehicleSelection;
        this.clickedVehicle = undefined;

        // Initialize shader sources
        this.vertexShaderSource = `
          precision highp float;

          // Input attributes for each vertex
          attribute vec2  a_position;            // Vertex position in the quad (-1 to +1)
          attribute vec2  a_texCoord;           // Generic texture coordinates
          attribute vec2  a_charCoord1;         // Texture coordinates for the first character
          attribute vec2  a_charCoord2;         // Texture coordinates for the second character
          attribute vec4  a_paintZone;          // Text drawing zone

          // Corner positions for each instance (high precision offset)
          attribute vec4 a_cornerPosition0;
          attribute vec4 a_cornerPosition1;
          attribute vec4 a_cornerPosition2;
          attribute vec4 a_cornerPosition3;

          // Uniforms for camera position and projection
          uniform vec4 u_eye_high;
          uniform vec4 u_eye_low;
          uniform mat4 u_matrix;

          // Variables passed to fragment shader
          varying vec2  v_texCoord;
          varying vec2  v_charCoord1; 
          varying vec2  v_charCoord2; 
          varying vec4  v_paintZone;

          void main() {
            //-----------------------------------------
            // 1. Corner selection by interpolation
            //-----------------------------------------
            // Transform a_position from [-1..1] to [0..1]
            // to perform bilinear interpolation
            // between the 4 corners (bottom-left, bottom-right, top-left, top-right).
            vec2 uv = (a_position + 1.0) * 0.5;

            // Horizontal interpolation (between left and right corners)
            // For the bottom "line" and for the top "line".
            vec4 bottomCorner = mix(a_cornerPosition0, a_cornerPosition1, uv.x);
            vec4 topCorner    = mix(a_cornerPosition2, a_cornerPosition3, uv.x);

            // Vertical interpolation (between bottom and top)
            vec4 instancePosition = mix(bottomCorner, topCorner, uv.y);

            //------------------------------------------------------
            // 2. Calculate final position by subtracting camera
            //------------------------------------------------------
            // High/low precision separation (simulated "double precision" technique in WebGL)
            // First subtract u_eye_high, then u_eye_low.
            gl_Position = vec4(
              vec3(instancePosition.rg, 0.0) - u_eye_high.xyz,
              0.0
            );
            gl_Position += vec4(
              vec3(instancePosition.ba, 0.0) - u_eye_low.xyz,
              0.0
            );

            //-------------------------------------
            // 3. Apply global matrix
            //-------------------------------------
            gl_Position = u_matrix * gl_Position;

            //---------------------------------------------------------
            // 4. Projection correction (depends on your setup)
            //---------------------------------------------------------
            // Ex.: add u_eye_high.w to gl_Position.w
            // if it contains, for example, the distance or offset
            // necessary for projection.
            gl_Position.w += u_eye_high.w;

            // Point size (if you use "point" rendering or a fallback)
            gl_PointSize = 20.0;

            //----------------------------------------------------
            // 5. Pass variables to fragment shader
            //----------------------------------------------------
            // a_texCoord: (0..1) on the quad
            v_texCoord     = a_texCoord;

            // Characters and associated info
            v_charCoord1   = a_charCoord1;
            v_charCoord2   = a_charCoord2;
            v_paintZone    = a_paintZone;
          }

    `;

        this.fragmentShaderSource = `
precision highp float;

// Uniforms for textures
uniform sampler2D u_vehicleTexture; // Vehicle texture for current render batch
uniform sampler2D u_textureLetters; // Texture for letters
uniform float u_letterGlyphScale; // Scale inside slot (atlas grid stays 6×6)
uniform float u_letterPadTop; // Top inset inside paint zone (0-0.45)
uniform float u_letterPadBottom; // Bottom inset inside paint zone (0-0.45)
uniform float u_letterPadSide; // Horizontal inset inside each half-slot (0-0.45)
uniform float u_letterCharGap; // Gap between dual chars as fraction of zone width (0-0.4)
uniform float u_letterPreserveAspect; // 1 = fit square glyphs in slot, 0 = legacy stretch

// Variables received from vertex shader
varying vec2  v_texCoord;     // Generic texture coordinates
varying vec2  v_charCoord1;   // Texture coordinates for the first character
varying vec2  v_charCoord2;   // Texture coordinates for the second character
varying vec4  v_paintZone;    // (s, t, p, q) limits of the zone where characters are painted

// Keep square atlas glyphs when the paint zone slot is not 1:1 (e.g. wide boat deck).
vec2 fitSquareGlyphInSlot(vec2 slotCoord, float slotAspect) {
  if (slotAspect >= 1.0) {
    float usedX = 1.0 / slotAspect;
    float xInset = (1.0 - usedX) * 0.5;
    return vec2(xInset + slotCoord.x * usedX, slotCoord.y);
  }
  float usedY = slotAspect;
  float yInset = (1.0 - usedY) * 0.5;
  return vec2(slotCoord.x, yInset + slotCoord.y * usedY);
}

float mapLetterAxis(float coord, float padStart, float padEnd) {
  return clamp((coord - padStart) / max(1.0 - padStart - padEnd, 0.01), 0.0, 1.0);
}

vec2 mapGlyphTexCoord(vec2 slotCoord, float slotAspect) {
  vec2 fitted = u_letterPreserveAspect > 0.5
    ? fitSquareGlyphInSlot(slotCoord, slotAspect)
    : slotCoord;
  if (u_letterGlyphScale <= 1.0) {
    float inset = (1.0 - u_letterGlyphScale) * 0.5;
    return inset + fitted * u_letterGlyphScale;
  }
  // Scale > 1: zoom into atlas cell center; margin avoids clipping wide glyphs (e.g. M) at cell edges.
  const float ATLAS_ZOOM_MARGIN = 0.9;
  float atlasSpan = (1.0 / u_letterGlyphScale) * ATLAS_ZOOM_MARGIN;
  return 0.5 + (fitted - 0.5) * atlasSpan;
}

void main() {
  // --- 1. Determine base vehicle color ---
  vec4 vehicleColor = texture2D(u_vehicleTexture, v_texCoord);

  // --- 2. Check paint zone and vehicle type ---
  bool canPaintLetters = (v_paintZone.t > v_paintZone.s && v_paintZone.q > v_paintZone.p);
  bool inPaintZone = (v_texCoord.x >= v_paintZone.s && v_texCoord.x <= v_paintZone.t &&
                      v_texCoord.y >= v_paintZone.p && v_texCoord.y <= v_paintZone.q);

  if (!canPaintLetters || !inPaintZone) {
    // Simple alpha-based antialiasing for vehicles
    float alpha = smoothstep(0.3, 0.7, vehicleColor.a);
    gl_FragColor = vec4(vehicleColor.rgb, alpha);
    return;
  }

  vehicleColor = vec4(1.0, 1.0, 1.0, 1.0);

  // --- 3. Calculate relative texture coordinates within the paint zone ---
  float w = v_paintZone.t - v_paintZone.s;
  float h = v_paintZone.q - v_paintZone.p;

  vec2 zoneTexCoord = (v_texCoord - vec2(v_paintZone.s, v_paintZone.p)) / vec2(w, h);
  zoneTexCoord.y = 1.0 - zoneTexCoord.y;
  const float LETTER_ATLAS_GRID = 6.0;
  float letterY = mapLetterAxis(zoneTexCoord.y, u_letterPadTop, u_letterPadBottom);
  float singleSlotAspect = (w * 0.5) / h;
  float dualSlotWidth = (1.0 - u_letterCharGap) * 0.5;
  float dualSlotAspect = (w * dualSlotWidth) / h;

  // --- 4. Handle number of characters to paint ---
  bool singleChar = (v_charCoord2.x == 0.0 && v_charCoord2.y == 0.0);

  // --- 5. Paint character(s) ---
  if (singleChar) {
    if (zoneTexCoord.x > 0.25 && zoneTexCoord.x < 0.75) {
      float rawX = (zoneTexCoord.x - 0.25) * 2.0;
      float letterX = mapLetterAxis(rawX, u_letterPadSide, u_letterPadSide);
      vec2 charTexCoord = mapGlyphTexCoord(vec2(letterX, letterY), singleSlotAspect);
      vec2 charCoord = v_charCoord1 + (charTexCoord / LETTER_ATLAS_GRID);
      vec4 charColor = texture2D(u_textureLetters, charCoord);

      gl_FragColor = mix(vehicleColor, charColor, charColor.a);
    } else {
      gl_FragColor = vehicleColor;
    }
    return;
  }

  // If there are two characters (50/50 split when charGap is 0 — same as bus)
  if (zoneTexCoord.x < dualSlotWidth) {
    float rawX = zoneTexCoord.x / dualSlotWidth;
    float letterX = mapLetterAxis(rawX, u_letterPadSide, u_letterPadSide);
    vec2 charTexCoord = mapGlyphTexCoord(vec2(letterX, letterY), dualSlotAspect);
    vec2 charCoord = v_charCoord1 + (charTexCoord / LETTER_ATLAS_GRID);
    vec4 charColor = texture2D(u_textureLetters, charCoord);

    gl_FragColor = mix(vehicleColor, charColor, charColor.a);
  } else if (zoneTexCoord.x > dualSlotWidth + u_letterCharGap) {
    float rawX = (zoneTexCoord.x - dualSlotWidth - u_letterCharGap) / dualSlotWidth;
    float letterX = mapLetterAxis(rawX, u_letterPadSide, u_letterPadSide);
    vec2 charTexCoord = mapGlyphTexCoord(vec2(letterX, letterY), dualSlotAspect);
    vec2 charCoord = v_charCoord2 + (charTexCoord / LETTER_ATLAS_GRID);
    vec4 charColor = texture2D(u_textureLetters, charCoord);

    gl_FragColor = mix(vehicleColor, charColor, charColor.a);
  } else {
    gl_FragColor = vehicleColor;
  }
  
  // Simple alpha-based antialiasing
  float alpha = smoothstep(0.1, 0.2, gl_FragColor.a);
  gl_FragColor = vec4(gl_FragColor.rgb, alpha);
}    `;
    }

    onAdd( map: maplibregl.Map, gl: WebGLRenderingContext ) {
        this.map = map;
        this.gl = gl as WebGL2RenderingContext;

        // Initialize shaders, programs and buffers
        try {
            this.initShaders();
            this.initBuffers();
            this.loadTextures();
        } catch( e ) {
            console.error( e );

            throw e;
        }

        this.ready = true;

        // Initialize FPS throttling based on current zoom if adaptive config is set
        if ( this.zoomFpsConfig !== null ) {
            this.updateFpsForCurrentZoom();
        }

        // Use events emitted by MapLibre to handle context loss/restoration
        this.map.on( "webglcontextlost", this.handleContextLost );
        this.map.on( "webglcontextrestored", this.handleContextRestored );

        // Handle camera movements
        this.map.on( "move", () => {
            this.cameraMoved = true;
        } );
        this.map.on( "zoom", () => {
            this.cameraMoved = true;
        } );
        this.map.on( "zoomend", () => {
            this.updateFpsForCurrentZoom();
        } );
        this.map.on( "rotate", () => {
            this.cameraMoved = true;
        } );
        this.map.on( "pitch", () => {
            this.cameraMoved = true;
        } );

        this.map.on( "mousemove", ( e ) => {
            if ( this.hitVehicle( e.point ) !== undefined ) {
                // Change cursor when mouse hovers over the layer
                this.map.getCanvas().style.cursor = "pointer";
            } else {
                // Restore default cursor
                this.map.getCanvas().style.cursor = "grab";
            }
        } );

        /*
         * Event handler for clicks on the vehicle layer
         * Note: Custom layers WebGL cannot be targeted with layer filter, so we listen to all clicks
         * and check manually if a vehicle was hit
         */
        this.map.on( "click", ( e ) => {
            const hitVehicle = this.hitVehicle( e.point );

            if ( hitVehicle !== undefined ) {
                const vehicle = hitVehicle;
                console.debug( "[VehicleCustomLayer] Vehicle clicked:", {
                    "id":    vehicle.id,
                    "label": vehicle.label,
                    "type":  vehicle.type,
                    "color": vehicle.color,
                } );

                this.clickedVehicle = vehicle;
                this.resetPublishedVehicleSelection();
                this.refreshPopupContent();
                this.onVehicleSelectionChange?.( {
                    "tripId": vehicle.id,
                    "color":  vehicle.color,
                } );
            } else if ( this.clickedVehicle !== undefined ) {
                this.clearSelection();
            }
        } );
        this.map.on( "dblclick", ( e ) => {
            // console.log( "DOUBLE click on vehicle layer", e.lngLat );
            if ( !__IS_DEV__ ) {
                return;
            }
            e.preventDefault();

            // Proximity threshold to detect a click on a vehicle (screen space)
            this.clickedVehicle = this.hitVehicle( e.point );

            if ( this.clickedVehicle !== undefined ) {
                // this.vehicleManager.visualizeTrips( this.clickedVehicle );
            }
        } );
    }

    /**
     * Clears the selected vehicle and notifies listeners (same outcome as clicking away from the vehicle).
     */
    clearSelection(): void {
        if ( this.clickedVehicle === undefined ) {
            return;
        }

        this.clickedVehicle = undefined;
        this.resetPublishedVehicleSelection();
        this.onVehicleSelectionChange?.( null );
    }

    /**
     * Clears cached selection snapshots so the next publish always reaches React.
     */
    private resetPublishedVehicleSelection(): void {
        this.lastPublishedSelectionLngLat = null;
        this.lastPublishedSelectionContent = undefined;
        this.hasPublishedClearedSelection = false;
    }

    /**
     * Recomputes popup content from the currently selected vehicle's realtime data.
     * Called on click and during each render frame so the popup stays up-to-date.
     */
    refreshPopupContent(): void {
        if ( this.clickedVehicle === undefined ) {
            return;
        }

        const vehicle = this.clickedVehicle;
        const cache = this.vehicleManager.getRealtimeCache();
        const schedule = cache !== null ? cache.schedules.get( vehicle.id ) : undefined;

        if ( cache !== null && schedule !== undefined ) {
            const now = new Date();
            const nowSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
            const delay = cache.delays.get( vehicle.id );
            const nextStopIndex = findNextStopIndexDelayAware( schedule.data.stops, delay, nowSeconds );
            const stopAnchor = nextStopIndex >= 0 ? schedule.data.stops[nextStopIndex] : undefined;

            if ( stopAnchor !== undefined ) {
                const delaySec = getDelayForStopSequence( delay, stopAnchor.seq );
                const normalizedArrival = getNormalizedArrivalSecondsWithDelay( stopAnchor.arr, delaySec );

                const hours = Math.floor( normalizedArrival / 3600 ) % 24;
                const minutes = Math.floor( ( normalizedArrival % 3600 ) / 60 );
                const seconds = normalizedArrival % 60;
                const arrivalTime = `${ String( hours ).padStart( 2, "0" ) }:${ String( minutes ).padStart( 2, "0" ) }:${ String( seconds ).padStart( 2, "0" ) }`;

                const delayStr = delay !== undefined ? `(${ secondsToMinutes( delaySec ) } min)` : "";

                this.popupContent = {
                    "type":                   vehicle.type,
                    "arrival_time":           arrivalTime,
                    "delay":                  delayStr,
                    ...delay !== undefined ? { "delaySec": delaySec } : {},
                    "label":                  vehicle.label,
                    "stop_name":              stopAnchor.name,
                    "trip_headsign":          schedule.data.trip_headsign,
                    "route_type_france":      schedule.data.route_type_france ?? null,
                    "trip_headsign_original": schedule.data.trip_headsign_original ?? null,
                    "color":                  vehicle.color,
                };
            } else {
                this.popupContent = {
                    "type":                   vehicle.type,
                    "arrival_time":           "",
                    "delay":                  "",
                    "label":                  vehicle.label,
                    "stop_name":              "Aucun trajet trouvé",
                    "trip_headsign":          "",
                    "route_type_france":      schedule.data.route_type_france ?? null,
                    "trip_headsign_original": schedule.data.trip_headsign_original ?? null,
                    "color":                  vehicle.color,
                };
            }
        } else {
            this.popupContent = {
                "type":          vehicle.type,
                "arrival_time":  "",
                "delay":         "",
                "label":         vehicle.label,
                "stop_name":     "Aucun trajet trouvé",
                "trip_headsign": "",
                "color":         vehicle.color,
            };
        }
    }

    onRemove() {
    // Remove event listeners on the map when removing the layer
        this.map.off( "webglcontextlost", this.handleContextLost );
        this.map.off( "webglcontextrestored", this.handleContextRestored );

        for ( const { texture } of this.vehicleTextureCache.values() ) {
            this.gl.deleteTexture( texture );
        }
        this.vehicleTextureCache.clear();
        this.vehicleTextureBuilds.clear();
        this.textureDescriptorByKey.clear();
        this.vehicleTextureKeyById.clear();
    }

    /**
     * Padding multiplier applied on top of the on-screen icon half-size for hit testing.
     * @param type Vehicle type from domain model.
     * @returns Factor ≥ 1 enlarging the clickable quad beyond the drawn sprite.
     */
    private getVehicleHitPaddingMultiplier( type: VehicleType ): number {
        if ( type === VehicleType.Ferry || type === VehicleType.Water ) {
            return VEHICLE_HIT_PADDING_BOAT;
        }

        return VEHICLE_HIT_PADDING;
    }

    /**
     * Returns whether a screen point lies inside the vehicle icon quad (rotated with bearing).
     * Uses the same Mercator half-size as rendering so large sprites (boat, train) get matching hit areas.
     *
     * @param vehicle Vehicle under test.
     * @param clickPoint Pointer position in CSS pixels relative to the map container.
     * @param timestampMs Current time for position interpolation.
     * @returns True when the point falls inside the padded icon bounds.
     */
    private hitTestVehicleAtPoint(
        vehicle: Vehicle,
        clickPoint: maplibregl.Point,
        timestampMs: number,
    ): boolean {
        const position = this.getVehiclePositionForRender( vehicle, timestampMs );
        if ( position === null ) {
            return false;
        }

        const zoom = this.map.getZoom();
        let halfSizeMercator = this.primaryWidthDegrees( zoom ) / 600;
        halfSizeMercator *= this.getVehicleIconSizeMultiplier( vehicle.type );

        if ( halfSizeMercator <= 0 ) {
            return false;
        }

        const centerMercator = maplibregl.MercatorCoordinate.fromLngLat( [ position.longitude, position.latitude ] );
        const centerPx = this.map.project( new maplibregl.LngLat( position.longitude, position.latitude ) );

        const eastMercator = new maplibregl.MercatorCoordinate(
            centerMercator.x + halfSizeMercator,
            centerMercator.y,
            centerMercator.z,
        );
        const eastPx = this.map.project( eastMercator.toLngLat() );
        const halfSizePx = Math.hypot( eastPx.x - centerPx.x, eastPx.y - centerPx.y );

        const angle = ( position.bearing ?? 0 ) * ( Math.PI / 180 ) + Math.PI;
        const cosA = Math.cos( -angle );
        const sinA = Math.sin( -angle );

        const dx = clickPoint.x - centerPx.x;
        const dy = clickPoint.y - centerPx.y;
        const localX = ( dx * cosA ) - ( dy * sinA );
        const localY = ( dx * sinA ) + ( dy * cosA );

        const limit = halfSizePx * this.getVehicleHitPaddingMultiplier( vehicle.type );

        return Math.abs( localX ) <= limit && Math.abs( localY ) <= limit;
    }

    /**
     * Detect if a vehicle is under the pointer using the on-screen icon quad so the hit area
     * scales with type-specific icon size multipliers (e.g. ferry at 2×).
     *
     * @param clickPoint Mouse position in CSS pixels relative to the map container
     * @returns the closest vehicle whose icon bounds contain the point, or undefined
     */
    private hitVehicle( clickPoint: maplibregl.Point ): Vehicle | undefined {
        const timestampMs = Date.now();
        let minDistanceSq = Infinity;

        let hitVehicle: Vehicle | undefined;

        this.vehicles.forEach( ( vehicle ) => {
            if ( !this.hitTestVehicleAtPoint( vehicle, clickPoint, timestampMs ) ) {
                return;
            }

            const position = this.getVehiclePositionForRender( vehicle, timestampMs );
            if ( position === null ) {
                return;
            }

            const vehiclePixel = this.map.project( new maplibregl.LngLat( position.longitude, position.latitude ) );
            const dx = vehiclePixel.x - clickPoint.x;
            const dy = vehiclePixel.y - clickPoint.y;
            const distanceSq = dx * dx + dy * dy;

            if ( distanceSq < minDistanceSq ) {
                minDistanceSq = distanceSq;
                hitVehicle = vehicle;
            }
        } );

        return hitVehicle;
    }

    /**
     * Returns the realtime position used for rendering.
     */
    private getVehiclePositionForRender(
        vehicle: Vehicle,
        timestampMs: number,
        logFallbackForSelected: boolean = false,
    ): {
        "latitude":  number;
        "longitude": number;
        "bearing"?:  number;
    } | null {
        const realtimePosition = this.vehicleManager.getRealtimePosition( vehicle.id, timestampMs );
        if ( realtimePosition !== null ) {
            return {
                "latitude":  realtimePosition.lat,
                "longitude": realtimePosition.lon,
                "bearing":   realtimePosition.bearing,
            };
        }

        if ( logFallbackForSelected ) {
            this.logSelectedVehicleFallback( vehicle.id, timestampMs );
        }

        return null;
    }

    private logSelectedVehicleFallback(
        tripId: string,
        timestampMs: number,
    ): void {
        const nowMs = Date.now();
        const lastLogMs = this.fallbackLogLastMsByTrip.get( tripId ) ?? 0;
        if ( nowMs - lastLogMs < this.fallbackLogThrottleMs ) {
            return;
        }
        this.fallbackLogLastMsByTrip.set( tripId, nowMs );

        const cache = this.vehicleManager.getRealtimeCache();
        const scheduleEntry = cache?.schedules.get( tripId );
        const timeline = cache?.timelines.get( tripId );
        const delay = cache?.delays.get( tripId );
        const now = new Date( timestampMs );
        const nowLocalSeconds =
            now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds() + now.getMilliseconds() / 1000;

        console.warn( "[VehicleCustomLayer] Selected vehicle has no realtime position", {
            tripId,
            "hasRealtimeCache": cache !== null,
            "hasSchedule":      scheduleEntry !== undefined,
            "hasTimeline":      timeline !== undefined,
            "timelineLength":   timeline?.length ?? 0,
            "hasDelay":         delay !== undefined,
            "delayGlobal":      delay?.global ?? null,
            nowLocalSeconds,
            "fallbackDisabled": true,
        } );
    }

    /**
     * Forwards vehicle selection snapshots to React only when coordinates or popup content changed.
     *
     * @param lngLat Vehicle coordinates or the layer's cleared-selection sentinel.
     * @param content Popup content when a vehicle is selected.
     */
    private publishVehicleSelectionIfChanged(
        lngLat: [number, number],
        content?: PopupContent,
    ): void {
        const isClearedSelection = content === undefined &&
          lngLat[0] === 0 &&
          lngLat[1] === 0;

        if ( isClearedSelection && this.hasPublishedClearedSelection ) {
            return;
        }

        const lngLatUnchanged = this.lastPublishedSelectionLngLat !== null &&
          areVehicleSelectionLngLatEqual( this.lastPublishedSelectionLngLat, lngLat );
        const contentUnchanged = this.lastPublishedSelectionContent === content ||
          (
              this.lastPublishedSelectionContent !== undefined &&
              content !== undefined &&
              isSamePopupContent( this.lastPublishedSelectionContent, content )
          );

        if ( lngLatUnchanged && contentUnchanged ) {
            return;
        }

        this.lastPublishedSelectionLngLat = [ lngLat[0], lngLat[1] ];
        this.lastPublishedSelectionContent = content;
        this.hasPublishedClearedSelection = isClearedSelection;
        this.updateVehicleSelection( lngLat, content );
    }

    render( gl: WebGLRenderingContext, args: number[] | Record<string, unknown> ) {
        const matrix: number[] = Array.isArray( args ) ? args : ( args as { "defaultProjectionData": { "mainMatrix": number[] } } ).defaultProjectionData.mainMatrix;
        if ( this.cameraMoved ) {
            const eye = this.calculateEyePosition( matrix );
            this.eye_high = eye.high;
            this.eye_low = eye.low;
            this.cameraMoved = false;
        }

        this.drawVehicles( matrix );

        if ( this.clickedVehicle !== undefined ) {
            // Refresh selection data every frame so RT updates (next stop, delay) reach the Dynamic Island.
            this.refreshPopupContent();

            const position = this.getVehiclePositionForRender( this.clickedVehicle, Date.now(), true );
            if ( position != null && this.popupContent !== undefined ) {
                const lngLat: [number, number] = [ position.longitude, position.latitude ];
                this.publishVehicleSelectionIfChanged( lngLat, this.popupContent );
                if ( this.onSelectedVehicleFramePosition !== null ) {
                    const framePositionCallback = this.onSelectedVehicleFramePosition;
                    queueMicrotask( () => {
                        framePositionCallback( lngLat );
                    } );
                }
            } else if ( this.popupContent !== undefined ) {
                this.publishVehicleSelectionIfChanged( [ 0, 0 ], this.popupContent );
            } else {
                this.publishVehicleSelectionIfChanged( [ 0, 0 ], undefined );
            }
        } else {
            this.publishVehicleSelectionIfChanged( [ 0, 0 ], undefined );
        }
    }

    private initShaders() {
        const gl = this.gl;

        // Compile shaders
        const vertexShader = this.compileShader( gl.VERTEX_SHADER, this.vertexShaderSource );
        const fragmentShader = this.compileShader( gl.FRAGMENT_SHADER, this.fragmentShaderSource );

        // Create program and link shaders
        const program = gl.createProgram();
        if ( program == null ) {
            throw new Error( "Unable to create WebGL program" );
        }
        this.program = program;

        gl.attachShader( this.program, vertexShader );
        gl.attachShader( this.program, fragmentShader );
        gl.linkProgram( this.program );

        // Check linkage status
        if ( gl.getProgramParameter( this.program, gl.LINK_STATUS ) === false ) {
            const info = gl.getProgramInfoLog( this.program );

            throw new Error( `Error during program linkage: ${ info }` );
        }
    }

    /**
     * Function to format shader errors for better readability
     */
    private formatShaderError( errorLog: string, source: string ) {
        const lines = source.split( "\n" );
        const formattedErrorLog = errorLog.
            split( "\n" ).
            map( ( errorLine ) => {
                const match = errorLine.match( /ERROR: \d+:(\d+): (.+)/ );
                if ( match !== null ) {
                    const lineNumber = parseInt( match[1], 10 );
                    const errorMessage = match[2];
                    const sourceLine = lines[lineNumber - 1].trim();

                    return `Line ${ lineNumber }: ${ errorMessage }\nSource code: ${ sourceLine }`;
                }

                return errorLine;
            } ).
            join( "\n" );

        return formattedErrorLog;
    }

    private compileShader( type: number, source: string ): WebGLShader {
        const gl = this.gl;
        const shader = gl.createShader( type );
        if ( shader == null ) {
            throw new Error( "Unable to create shader" );
        }
        gl.shaderSource( shader, source );
        gl.compileShader( shader );

        // Check compilation
        if ( gl.getShaderParameter( shader, gl.COMPILE_STATUS ) === false ) {
            const errorLog = gl.getShaderInfoLog( shader );
            let error;
            if ( errorLog !== null ) {
                error = `Shader compilation error ${ type === gl.VERTEX_SHADER ? "vertex" : "fragment" }:\n${ this.formatShaderError( errorLog, source ) }`;
            } else {
                error = `Shader compilation error \n${ source }`;
            }
            gl.deleteShader( shader );

            throw new Error( error );
        }

        return shader;
    }

    private initBuffers() {
        const gl = this.gl;

        // Buffer for quad vertex positions
        const positionBuffer = gl.createBuffer();
        if ( positionBuffer == null ) {
            throw new Error( "Unable to create buffer for positions" );
        }
        this.positionBuffer = positionBuffer;
        gl.bindBuffer( gl.ARRAY_BUFFER, this.positionBuffer );
        const positions = new Float32Array( [
            -1.0,
            -1.0, // Bottom left corner
            1.0,
            -1.0, // Bottom right corner
            -1.0,
            1.0, // Top left corner
            1.0,
            1.0, // Top right corner
        ] );
        gl.bufferData( gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW );

        // Buffer for texture coordinates
        const texCoordBuffer = gl.createBuffer();
        if ( texCoordBuffer == null ) {
            throw new Error( "Unable to create buffer for texture coordinates" );
        }
        this.texCoordBuffer = texCoordBuffer;
        gl.bindBuffer( gl.ARRAY_BUFFER, this.texCoordBuffer );
        const texCoords = new Float32Array( [
            0.0,
            0.0, // bottom left
            1.0,
            0.0, // bottom right
            0.0,
            1.0, // top left
            1.0,
            1.0, // top right
        ] );
        gl.bufferData( gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW );

        // Initialize vehicle data
        this.initializeVehicleData();

        // Buffer for instance corner positions
        const instanceCornerPositionBuffer = gl.createBuffer();
        if ( instanceCornerPositionBuffer == null ) {
            throw new Error( "Unable to create buffer for instance corner positions" );
        }
        this.instanceCornerPositionBuffer = instanceCornerPositionBuffer;
        gl.bindBuffer( gl.ARRAY_BUFFER, this.instanceCornerPositionBuffer );
        gl.bufferData( gl.ARRAY_BUFFER, this.instanceCornerPositions, gl.DYNAMIC_DRAW );

        // Buffer for character texture coordinates
        const charCoordBuffer1 = gl.createBuffer();
        if ( charCoordBuffer1 == null ) {
            throw new Error( "Unable to create buffer for character texture coordinates (1)" );
        }
        this.charCoordBuffer1 = charCoordBuffer1;
        gl.bindBuffer( gl.ARRAY_BUFFER, this.charCoordBuffer1 );
        gl.bufferData( gl.ARRAY_BUFFER, this.charTextureCoords1, gl.STATIC_DRAW );

        const charCoordBuffer2 = gl.createBuffer();
        if ( charCoordBuffer2 == null ) {
            throw new Error( "Unable to create buffer for character texture coordinates (2)" );
        }
        this.charCoordBuffer2 = charCoordBuffer2;
        gl.bindBuffer( gl.ARRAY_BUFFER, this.charCoordBuffer2 );
        gl.bufferData( gl.ARRAY_BUFFER, this.charTextureCoords2, gl.STATIC_DRAW );

        // Buffer for paint zones
        const paintZoneBuffer = gl.createBuffer();
        if ( paintZoneBuffer == null ) {
            throw new Error( "Unable to create buffer for paint zones" );
        }
        this.paintZoneBuffer = paintZoneBuffer;
        gl.bindBuffer( gl.ARRAY_BUFFER, this.paintZoneBuffer );
        gl.bufferData( gl.ARRAY_BUFFER, this.paintZones, gl.STATIC_DRAW );
    }


    /**
     * Generates an SDF texture specifically for text (black on white)
     * @param image The source image containing the text
     * @param spread SDF propagation distance in pixels
     * @returns A canvas containing the SDF texture
     */
    private generateTextSDFTexture( image: HTMLImageElement, spread: number = 4 ): HTMLCanvasElement {
        const canvas = document.createElement( "canvas" );
        const ctx = canvas.getContext( "2d" );
        if ( ctx == null ) {
            throw new Error( "Unable to create 2D context" );
        }

        canvas.width = image.width;
        canvas.height = image.height;

        // Draw original image
        ctx.drawImage( image, 0, 0 );

        // Get image data
        const imageData = ctx.getImageData( 0, 0, canvas.width, canvas.height );
        const data = imageData.data;
        const width = canvas.width;
        const height = canvas.height;

        // Create binary map based on luminance or alpha
        const binaryMap = new Uint8Array( width * height );
        for ( let y = 0; y < height; y++ ) {
            for ( let x = 0; x < width; x++ ) {
                const idx = ( y * width + x ) * 4;
                const alpha = data[idx + 3];

                // If image uses alpha (transparent background)
                if ( alpha < 255 ) {
                    binaryMap[y * width + x] = alpha > 128 ? 1 : 0;
                } else {
                    // Otherwise, use luminance (black on white)
                    const luminance = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];

                    // Black = inside (1), white = outside (0)
                    binaryMap[y * width + x] = luminance < 128 ? 1 : 0;
                }
            }
        }

        // Calculate distance transform
        const distanceField = this.computeDistanceField( binaryMap, width, height, spread );

        // Create new SDF image
        const sdfImageData = ctx.createImageData( width, height );
        const sdfData = sdfImageData.data;

        for ( let y = 0; y < height; y++ ) {
            for ( let x = 0; x < width; x++ ) {
                const idx = y * width + x;
                const pixelIdx = idx * 4;

                // Get original to preserve colors if necessary
                const originalR = data[pixelIdx];
                const originalG = data[pixelIdx + 1];
                const originalB = data[pixelIdx + 2];

                // Get normalized distance
                const distance = distanceField[idx];

                // Convert to 0-255 value
                const sdfValue = Math.round( distance * 255 );

                // Keep original colors in RGB, put SDF in alpha
                sdfData[pixelIdx] = originalR; // R (original color)
                sdfData[pixelIdx + 1] = originalG; // G (original color)
                sdfData[pixelIdx + 2] = originalB; // B (original color)
                sdfData[pixelIdx + 3] = sdfValue; // A (distance field)
            }
        }

        ctx.putImageData( sdfImageData, 0, 0 );

        return canvas;
    }

    /**
     * Calculates the euclidean distance field
     * @param binaryMap Binary map (0 = outside, 1 = inside)
     * @param width Image width
     * @param height Image height
     * @param maxDistance Maximum propagation distance
     * @returns An array of normalized distances [0, 1]
     */
    private computeDistanceField( binaryMap: Uint8Array, width: number, height: number, maxDistance: number ): Float32Array {
        const size = width * height;
        const distanceField = new Float32Array( size );

        // Initialize with infinite distances
        const INF = width + height;

        /*
         * Distance transform in two passes
         * Pass 1: from top to bottom, left to right
         */
        for ( let y = 0; y < height; y++ ) {
            for ( let x = 0; x < width; x++ ) {
                const idx = y * width + x;

                if ( binaryMap[idx] === 1 ) {
                    // Inside pixel
                    let minDist = INF;

                    // Find distance to nearest edge
                    for ( let dy = -maxDistance; dy <= maxDistance; dy++ ) {
                        for ( let dx = -maxDistance; dx <= maxDistance; dx++ ) {
                            const ny = y + dy;
                            const nx = x + dx;

                            if ( ny >= 0 && ny < height && nx >= 0 && nx < width ) {
                                const nidx = ny * width + nx;
                                if ( binaryMap[nidx] === 0 ) {
                                    const dist = Math.sqrt( dx * dx + dy * dy );
                                    minDist = Math.min( minDist, dist );
                                }
                            }
                        }
                    }

                    // Store positive distance (inside)
                    distanceField[idx] = Math.min( minDist, maxDistance ) / maxDistance;
                } else {
                    // Outside pixel
                    let minDist = INF;

                    // Find distance to nearest edge
                    for ( let dy = -maxDistance; dy <= maxDistance; dy++ ) {
                        for ( let dx = -maxDistance; dx <= maxDistance; dx++ ) {
                            const ny = y + dy;
                            const nx = x + dx;

                            if ( ny >= 0 && ny < height && nx >= 0 && nx < width ) {
                                const nidx = ny * width + nx;
                                if ( binaryMap[nidx] === 1 ) {
                                    const dist = Math.sqrt( dx * dx + dy * dy );
                                    minDist = Math.min( minDist, dist );
                                }
                            }
                        }
                    }

                    // Store negative distance (outside), mapped to [0, 0.5]
                    distanceField[idx] = Math.max( 0, 0.5 - Math.min( minDist, maxDistance ) / ( 2 * maxDistance ) );
                }
            }
        }

        return distanceField;
    }

    /**
     * Loads an image resource asynchronously.
     * @param url URL or object URL to load.
     * @returns Promise resolving with the decoded image.
     */
    private async loadImage( url: string ): Promise<HTMLImageElement> {
        return await new Promise( ( resolve, reject ) => {
            const image = new Image();
            image.src = url;
            image.onload = () => {
                resolve( image );
            };
            image.onerror = ( err: string | Event ) => {
                reject( new Error( typeof err === "string" ? err : "Failed to load image" ) );
            };
        } );
    }

    /**
     * Maps all incoming vehicle types to the effective render family.
     * @param type Vehicle type from domain model.
     * @returns One of the supported SVG families.
     */
    private resolveEffectiveVehicleType( type: VehicleType ): VehicleTextureFamily {
        if ( type === VehicleType.Ferry || type === VehicleType.Water ) {
            return "boat";
        }
        if (
            type === VehicleType.Bus ||
            type === VehicleType.Trolleybus ||
            type === VehicleType.Funicular
        ) {
            return "bus";
        }
        if ( type === VehicleType.Car || type === VehicleType.Taxi ) {
            return "car";
        }

        // Fallback for all non-covered types.
        return "tram";
    }

    /**
     * Returns the text paint zone to overlay line labels on the vehicle icon.
     * @param type Effective render family.
     * @returns Normalized UV rectangle `[left, right, top, bottom]`.
     */
    private getPaintZoneForType( type: VehicleTextureFamily ): [number, number, number, number] {
        if ( type === "bus" ) {
            return [ 0.28, 0.63, 0.4, 0.6 ];
        }
        if ( type === "tram" ) {
            return [ 0.5, 0.76, 0.42, 0.58 ];
        }
        if ( type === "boat" ) {
            // Narrow band centered on shortened white flat deck (x=95–160); ~1:1 half-slots.
            return [ 0.343, 0.522, 0.455, 0.545 ];
        }

        // Cars keep the current behavior: no line letters.
        return [ 0, 0, 0, 0 ];
    }

    /**
     * Returns shader layout for route labels drawn on a vehicle sprite.
     * @param type Effective render family.
     * @returns Grid divisor and vertical insets for the fragment shader.
     */
    private getLetterLabelLayoutForType( type: VehicleTextureFamily ): VehicleLetterLabelLayout {
        if ( type === "boat" ) {
            // Same atlas path as bus (grid 6×6, no zoom); aspect fit only for the wide deck band.
            return {
                "glyphScale":          1,
                "padTop":              0,
                "padBottom":           0,
                "padSide":             0,
                "charGap":             0,
                "preserveGlyphAspect": true,
            };
        }

        return {
            "glyphScale":          1,
            "padTop":              0,
            "padBottom":           0,
            "padSide":             0,
            "charGap":             0,
            "preserveGlyphAspect": false,
        };
    }

    /**
     * Recolors only the body color placeholders for a given SVG family.
     * @param svgSource Raw SVG template.
     * @param type Effective render family.
     * @param colorHex Final target body color in hex.
     * @returns Recolored SVG markup.
     */
    private recolorVehicleSvg( svgSource: string, type: VehicleTextureFamily, colorHex: string ): string {
        const lowerCaseHex = colorHex.toLowerCase();
        const baseColorsByType: Record<VehicleTextureFamily, string[]> = {
            "bus":  [ "#fd5312" ],
            "car":  [ "#11d39c", "#1155d3" ],
            "tram": [ "#6ce671" ],
            "boat": [ "#009ee0" ],
        };

        let recolored = svgSource;
        for ( const baseColor of baseColorsByType[type] ) {
            recolored = recolored.replace( new RegExp( baseColor, "gi" ), lowerCaseHex );
        }

        return recolored;
    }

    /**
     * Returns the map icon size multiplier for a vehicle type.
     * Wide sprites (boat) or long consist types (train, tram) need a boost to stay readable.
     * @param type Vehicle type from domain model.
     * @returns Multiplier applied to the base icon size in Mercator space.
     */
    private getVehicleIconSizeMultiplier( type: VehicleType ): number {
        if ( type === VehicleType.Train ) {
            return 3;
        }
        if ( type === VehicleType.Tram ) {
            return 1.45;
        }
        if ( type === VehicleType.Ferry || type === VehicleType.Water ) {
            return this.boatIconSizeMultiplier;
        }

        return 1;
    }

    /**
     * Rasterizes SVG markup into a canvas suitable for WebGL texture upload.
     * @param svg SVG markup to rasterize.
     * @returns Canvas containing rasterized pixels.
     */
    private async rasterizeSvgToCanvas( svg: string ): Promise<HTMLCanvasElement> {
        const blob = new Blob( [ svg ], { "type": "image/svg+xml" } );
        const url = URL.createObjectURL( blob );
        try {
            const image = await this.loadImage( url );
            const canvas = document.createElement( "canvas" );
            const sourceWidth = image.naturalWidth > 0 ? image.naturalWidth : 128;
            const sourceHeight = image.naturalHeight > 0 ? image.naturalHeight : 64;
            const targetSize = this.vehicleTextureCanvasSize;
            const effectiveTarget = targetSize * this.vehicleTextureCanvasPaddingRatio;
            const scale = Math.min( effectiveTarget / sourceWidth, effectiveTarget / sourceHeight );
            const drawWidth = sourceWidth * scale;
            const drawHeight = sourceHeight * scale;
            const offsetX = ( targetSize - drawWidth ) / 2;
            const offsetY = ( targetSize - drawHeight ) / 2;
            canvas.width = targetSize;
            canvas.height = targetSize;
            const ctx = canvas.getContext( "2d" );
            if ( ctx == null ) {
                throw new Error( "Unable to create 2D context" );
            }
            ctx.clearRect( 0, 0, targetSize, targetSize );

            // Draw with "contain" behavior to preserve aspect ratio.
            ctx.drawImage( image, offsetX, offsetY, drawWidth, drawHeight );

            return canvas;
        } finally {
            URL.revokeObjectURL( url );
        }
    }

    /**
     * Creates a WebGL texture from a prepared canvas.
     * @param canvas Source canvas.
     * @returns Created and configured WebGL texture.
     */
    private createTextureFromCanvas( canvas: HTMLCanvasElement ): WebGLTexture {
        const gl = this.gl;
        const texture = gl.createTexture();
        if ( texture == null ) {
            throw new Error( "Unable to create vehicle texture" );
        }
        this.setupTextureFromCanvas( gl, canvas, texture );

        return texture;
    }

    /**
     * Updates an entry usage timestamp for LRU eviction.
     * @param textureKey Cache key of the texture entry.
     */
    private touchVehicleTextureEntry( textureKey: string ): void {
        const entry = this.vehicleTextureCache.get( textureKey );
        if ( entry === undefined ) {
            return;
        }
        entry.lastUsedMs = Date.now();
    }

    /**
     * Applies LRU eviction when the vehicle texture cache exceeds its size limit.
     */
    private evictVehicleTextureCacheIfNeeded(): void {
        const gl = this.gl;
        if ( this.vehicleTextureCache.size <= this.maxVehicleTextureCacheSize ) {
            return;
        }

        const entries = Array.from( this.vehicleTextureCache.entries() );
        entries.sort( ( [ , a ], [ , b ] ) => a.lastUsedMs - b.lastUsedMs );

        const toEvict = this.vehicleTextureCache.size - this.maxVehicleTextureCacheSize;
        for ( let i = 0; i < toEvict; i++ ) {
            const [ key, entry ] = entries[i];
            gl.deleteTexture( entry.texture );
            this.vehicleTextureCache.delete( key );
        }
    }

    /**
     * Ensures a vehicle texture exists for a given cache key.
     * Uses in-flight deduplication (if multiple call are made on the texture before it finished loading, do not rebuild the texture) to avoid duplicate rasterization work.
     * @param textureKey Stable cache key.
     * @param effectiveType Effective render family.
     * @param color Final color used for recoloring.
     * @returns Promise resolved when the texture is ready in cache.
     */
    private async ensureVehicleTexture( textureKey: string, effectiveType: VehicleTextureFamily, color: Color ): Promise<void> {
        if ( this.vehicleTextureCache.has( textureKey ) ) {
            this.touchVehicleTextureEntry( textureKey );

            return;
        }
        const inFlight = this.vehicleTextureBuilds.get( textureKey );
        if ( inFlight !== undefined ) {
            await inFlight;

            return;
        }

        const buildPromise = ( async() => {
            const template = this.svgTemplateByType.get( effectiveType );
            if ( template === undefined ) {
                throw new Error( `Missing SVG template for vehicle type '${ effectiveType }'` );
            }
            const recoloredSvg = this.recolorVehicleSvg( template, effectiveType, ( colorTools.colorToHex as ( input: Color ) => string )( color ) );
            const canvas = await this.rasterizeSvgToCanvas( recoloredSvg );
            const texture = this.createTextureFromCanvas( canvas );
            this.vehicleTextureCache.set( textureKey, {
                texture,
                "lastUsedMs": Date.now(),
            } );
            this.evictVehicleTextureCacheIfNeeded();
        } )();

        this.vehicleTextureBuilds.set( textureKey, buildPromise );
        try {
            await buildPromise;
        } finally {
            this.vehicleTextureBuilds.delete( textureKey );
        }
    }

    /**
     * Computes the normalized cache descriptor for a vehicle.
     * @param vehicle Domain vehicle object.
     * @returns Cache key + effective type + final visible color.
     */
    private computeTextureKey( vehicle: Vehicle ): {
        "textureKey":    string;
        "effectiveType": VehicleTextureFamily;
        "color":         Color;
    } {
        const effectiveType = this.resolveEffectiveVehicleType( vehicle.type );
        const colorWithBlackOverride = colorTools.resolveVehicleIconDisplayColor( vehicle.color, vehicle.type );
        const quantizedColor = ( colorTools.quantizeColor as ( input: Color, step: number ) => Color )( colorWithBlackOverride, this.vehicleColorQuantizationStep );
        const visibleColor = ( colorTools.liftDarkColor as ( input: Color, minLuminance: number, maxBoost?: number ) => Color )( quantizedColor, this.minVehicleLuminance );
        const textureKey = `${ effectiveType }:${ ( colorTools.colorToHex as ( input: Color ) => string )( visibleColor ) }`;

        return {
            textureKey,
            effectiveType,
            "color": visibleColor,
        };
    }

    /**
     * Loads base SVG templates and the letters texture at layer startup.
     * Initializes the rendering pipeline readiness flag.
     */
    private loadTextures() {
        this.texturesLoaded = false;

        const fetchText = async( url: string ): Promise<string> => {
            const response = await fetch( url );
            if ( !response.ok ) {
                throw new Error( `Unable to fetch '${ url }': ${ response.status }` );
            }

            return await response.text();
        };

        Promise.all( [
            fetchText( "./assets/textures/bus.svg" ),
            fetchText( "./assets/textures/car.svg" ),
            fetchText( "./assets/textures/tram.svg" ),
            fetchText( "./assets/textures/boat.svg" ),
            this.loadImage( "./assets/textures/texture_atlas_letters.png" ),
        ] ).
            then( ( [ busSvg, carSvg, tramSvg, boatSvg, letterImage ] ) => {
                this.svgTemplateByType.set( "bus", busSvg );
                this.svgTemplateByType.set( "car", carSvg );
                this.svgTemplateByType.set( "tram", tramSvg );
                this.svgTemplateByType.set( "boat", boatSvg );

                const letterSDF = this.generateTextSDFTexture( letterImage, 1 );
                const textureLetters = this.gl.createTexture();
                if ( textureLetters == null ) {
                    throw new Error( "Unable to create texture for letters" );
                }
                this.textureLetters = textureLetters;
                this.setupTextureFromCanvas( this.gl, letterSDF, this.textureLetters, this.textureUnitLetters );
                this.texturesLoaded = true;
                this.refreshData();
                animationOrchestrator.requestRepaint();
            } ).
            catch( ( err ) => {
                console.error( "Error loading textures:", err );
            } );
    }

    /**
     * Uploads canvas pixels to an existing WebGL texture with linear filtering.
     * @param gl Current WebGL2 context.
     * @param canvas Source canvas containing texture pixels.
     * @param texture Destination WebGL texture.
     * @param textureUnit Optional texture unit to activate before upload.
     */
    private setupTextureFromCanvas( gl: WebGL2RenderingContext, canvas: HTMLCanvasElement, texture: WebGLTexture, textureUnit?: number ) {
        if ( textureUnit !== undefined ) {
            gl.activeTexture( gl.TEXTURE0 + textureUnit );
        }
        gl.bindTexture( gl.TEXTURE_2D, texture );
        gl.pixelStorei( gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true );
        gl.texImage2D( gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas );
        gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE );
        gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE );
        gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR );
        gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR );
    }

    private calculateEyePosition( matrix: number[] ) {
    // Calculate u_eye_high and u_eye_low
        const mat: number[][] = [
            [ matrix[0], matrix[4], matrix[8], matrix[12] ],
            [ matrix[1], matrix[5], matrix[9], matrix[13] ],
            [ matrix[2], matrix[6], matrix[10], matrix[14] ],
            [ matrix[3], matrix[7], matrix[11], matrix[15] ],
        ];

        const eye = math.lusolve( mat, [ [ 0 ], [ 0 ], [ 0 ], [ 1 ] ] ) as number[][];

        const clipW = 1.0 / eye[3][0];
        const eyeDivided = math.divide( eye, eye[3][0] ) as number[];
        const eyeFlat = math.flatten( eyeDivided );

        eyeFlat[3] = clipW;

        const high = new Float32Array( eyeFlat.map( ( e: number ) => Math.fround( e ) ) );
        const low = new Float32Array( eyeFlat.map( ( e: number, i: number ) => e - high[i] ) );

        return {
            high,
            low,
        };
    }

    /**
     * Initialize buffers with initial capacity
     */
    private initializeVehicleData() {
        // console.log( "InitializeVehicleData" );

        const initialSize = this.maxBufferSize;

        // Initialize arrays with max capacity
        this.instanceCornerPositions = new Float32Array( initialSize * 16 );
        this.charTextureCoords1 = new Float32Array( initialSize * 2 );
        this.charTextureCoords2 = new Float32Array( initialSize * 2 );
        this.paintZones = new Float32Array( initialSize * 4 );
        this.vehicleTextureKeys = new Array<string>( initialSize );

        // Initialize all vehicles
        const vehicles = [ ...this.vehicleManager.getAllVehicles() ];
        vehicles.sort( ( left, right ) => {
            const leftKey = this.computeTextureKey( left ).textureKey;
            const rightKey = this.computeTextureKey( right ).textureKey;
            if ( leftKey !== rightKey ) {
                return leftKey.localeCompare( rightKey );
            }

            return left.id.localeCompare( right.id );
        } );
        this.vehicles = vehicles;
        this.vehicleTextureKeyById.clear();
        this.renderBatches = [];

        vehicles.forEach( ( vehicle, i ) => {
            this.vehicleIndexMap.set( vehicle.id, i );
            this.initializeVehicleAtIndex( vehicle, i );
        } );

        this.rebuildRenderBatches( vehicles.length );
        this.nextFreeIndex = vehicles.length;
    }

    /**
     * Initialize a single vehicle's data at a specific buffer index
     */
    private initializeVehicleAtIndex( vehicle: Vehicle, index: number ) {
        const descriptor = this.computeTextureKey( vehicle );
        this.vehicleTextureKeys[index] = descriptor.textureKey;
        this.vehicleTextureKeyById.set( vehicle.id, descriptor.textureKey );
        this.textureDescriptorByKey.set( descriptor.textureKey, {
            "effectiveType": descriptor.effectiveType,
            "color":         descriptor.color,
        } );
        if ( this.texturesLoaded ) {
            void this.ensureVehicleTexture( descriptor.textureKey, descriptor.effectiveType, descriptor.color ).
                then( () => {
                    animationOrchestrator.requestRepaint();
                } ).
                catch( ( error ) => {
                    console.error( "Unable to build vehicle SVG texture", error );
                } );
        }

        // Characters: only A–Z and 0–9 are in the atlas; ignore unsupported chars and take last 2 supported
        const alphabet = [ "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "0", "1", "2", "3", "4", "5", "6", "7", "8", "9" ];
        const label = vehicle.label;
        const supported = [ ...label ].filter( ( c ) => alphabet.includes( c.toUpperCase() ) );
        const char1 = supported.length >= 2 ? supported[supported.length - 2] : supported.length === 1 ? supported[0] : " ";
        const char2 = supported.length >= 2 ? supported[supported.length - 1] : " ";

        const charIndex1 = alphabet.indexOf( char1.toUpperCase() );
        const charIndex2 = alphabet.indexOf( char2.toUpperCase() );

        const validCharIndex1 = charIndex1 !== -1 ? charIndex1 : 0;

        // When only one character: put it in char1 and set char2 index to 0 so shader singleChar branch draws only char1 in center
        const validCharIndex2 = supported.length === 1 ? 0 : charIndex2 !== -1 ? charIndex2 : 0;

        const charAtlasCols = 6;
        const charAtlasRows = 6;
        const charAtlasWidth = 144;
        const charAtlasHeight = 150;
        const charSingleTextureWidth = charAtlasWidth / charAtlasCols;
        const charSingleTextureHeight = charAtlasHeight / charAtlasRows;

        const charRow1 = Math.floor( validCharIndex1 / charAtlasCols );
        const charCol1 = validCharIndex1 % charAtlasCols;

        this.charTextureCoords1[index * 2] = ( charCol1 * charSingleTextureWidth ) / charAtlasWidth;
        this.charTextureCoords1[index * 2 + 1] = ( charRow1 * charSingleTextureHeight ) / charAtlasHeight;

        const charRow2 = Math.floor( validCharIndex2 / charAtlasCols );
        const charCol2 = validCharIndex2 % charAtlasCols;

        this.charTextureCoords2[index * 2] = ( charCol2 * charSingleTextureWidth ) / charAtlasWidth;
        this.charTextureCoords2[index * 2 + 1] = ( charRow2 * charSingleTextureHeight ) / charAtlasHeight;

        this.paintZones.set( this.getPaintZoneForType( descriptor.effectiveType ), index * 4 );
    }

    /**
     * Rebuilds contiguous render batches grouped by identical texture key.
     * @param activeCount Number of active vehicle instances.
     */
    private rebuildRenderBatches( activeCount: number ): void {
        this.renderBatches = [];
        if ( activeCount <= 0 ) {
            return;
        }

        let batchStart = 0;
        let currentKey = this.vehicleTextureKeys[0];
        for ( let i = 1; i < activeCount; i++ ) {
            const key = this.vehicleTextureKeys[i];
            if ( key !== currentKey ) {
                this.renderBatches.push( {
                    "textureKey": currentKey,
                    "start":      batchStart,
                    "count":      i - batchStart,
                } );
                batchStart = i;
                currentKey = key;
            }
        }

        this.renderBatches.push( {
            "textureKey": currentKey,
            "start":      batchStart,
            "count":      activeCount - batchStart,
        } );
    }

    // Public method to force data refresh from VehicleManager
    public refreshData() {
        if ( !this.ready ) {
            return;
        }

        const vehicles = this.vehicleManager.getAllVehicles();

        // Check if vehicles list actually changed to avoid unnecessary rebuilds
        const currentVehicleIds = new Set( this.vehicles.map( ( v ) => v.id ) );
        const newVehicleIds = new Set( vehicles.map( ( v ) => v.id ) );

        // Only rebuild if vehicles were added or removed
        const vehiclesChanged =
            currentVehicleIds.size !== newVehicleIds.size ||
            !Array.from( currentVehicleIds ).every( ( id ) => newVehicleIds.has( id ) );

        const staticDataChanged = vehicles.some( ( vehicle ) => this.vehicleTextureKeyById.get( vehicle.id ) !== this.computeTextureKey( vehicle ).textureKey );

        if ( !vehiclesChanged && !staticDataChanged && this.vehicles.length === vehicles.length ) {
            // No structural changes, just update positions for existing vehicles
            this.updateVehiclePositions();

            return;
        }

        // Set flag to prevent concurrent updateVehiclePositions() calls during rebuild
        this.isRefreshing = true;
        try {
            const sortedVehicles = [ ...vehicles ];
            sortedVehicles.sort( ( left, right ) => {
                const leftKey = this.computeTextureKey( left ).textureKey;
                const rightKey = this.computeTextureKey( right ).textureKey;
                if ( leftKey !== rightKey ) {
                    return leftKey.localeCompare( rightKey );
                }

                return left.id.localeCompare( right.id );
            } );

            // Ensure buffers are large enough for the active vehicles
            while ( sortedVehicles.length > this.maxBufferSize ) {
                this.expandBuffers();
            }

            // Rebuild a dense index map to avoid holes and missing renders
            const newVehicleIndexMap = new Map<string, number>();
            this.vehicleTextureKeyById.clear();
            this.renderBatches = [];

            sortedVehicles.forEach( ( vehicle, index ) => {
                newVehicleIndexMap.set( vehicle.id, index );

                // Initialize or refresh static data at the compacted index
                this.initializeVehicleAtIndex( vehicle, index );
                this.updateStaticBuffersAtIndex( index );
            } );
            this.rebuildRenderBatches( sortedVehicles.length );

            // Clear trailing dynamic data to avoid ghost corners after compaction
            const activeCornerLength = sortedVehicles.length * 16;
            if ( this.instanceCornerPositions.length > activeCornerLength ) {
                this.instanceCornerPositions.fill( 0, activeCornerLength );
            }

            // Update index map and vehicles array atomically to avoid race conditions
            this.vehicleIndexMap = newVehicleIndexMap;
            this.freeIndices = [];
            this.nextFreeIndex = sortedVehicles.length;

            // Update vehicles array for rendering
            this.vehicles = sortedVehicles;
        } finally {
            this.isRefreshing = false;
        }

        // Immediately update positions after rebuilding index to ensure vehicles are visible
        this.updateVehiclePositions();

        // Request map repaint via orchestrator (batched with other repaint requests)
        animationOrchestrator.requestRepaint();
    }

    /**
     * Configure adaptive FPS throttling based on zoom level.
     * The FPS is automatically adjusted when the map zoom changes (on zoomend event).
     * 
     * @param config - Array of [minZoom, fps] tuples sorted by zoom DESC, or null for no throttling.
     *                 When zoom is below all thresholds, the lowest threshold's FPS is used.
     * @example
     * // Configure adaptive FPS: higher FPS for close zoom, lower for overview
     * layer.setAdaptiveFpsConfig([
     *     [16, 30], // zoom >= 16: 30 FPS
     *     [14, 20], // zoom >= 14: 20 FPS
     *     [12, 10], // zoom < 14: 10 FPS
     * ]);
     * 
     * // Disable throttling
     * layer.setAdaptiveFpsConfig(null);
     */
    public setAdaptiveFpsConfig( config: ZoomFpsConfig ): void {
        this.zoomFpsConfig = config;

        // Immediately apply FPS for current zoom level if map is ready
        if ( this.ready ) {
            this.updateFpsForCurrentZoom();
        }
    }

    /**
     * Update the FPS throttling interval based on current zoom level.
     * Called on zoomend event to avoid recalculating during zoom animation.
     */
    private updateFpsForCurrentZoom(): void {
        const currentZoom = this.map.getZoom();
        const fps = this.getFpsForZoom( currentZoom );

        if ( fps <= 0 ) {
            this.minPositionUpdateIntervalMs = 0;
        } else {
            this.minPositionUpdateIntervalMs = 1000 / fps;
        }
    }

    /**
     * Find the appropriate FPS value for a given zoom level from the configuration.
     * 
     * @param zoom - Current map zoom level
     * @returns FPS value to use, or 0 if no throttling should be applied
     */
    private getFpsForZoom( zoom: number ): number {
        // No config means no throttling
        if ( this.zoomFpsConfig === null || this.zoomFpsConfig.length === 0 ) {
            return 0;
        }

        /*
         * Find the first threshold where zoom >= minZoom
         * Config should be sorted by zoom DESC (highest first)
         */
        for ( const [ minZoom, fps ] of this.zoomFpsConfig ) {
            if ( zoom >= minZoom ) {
                return fps;
            }
        }

        // Zoom is below all thresholds: use the lowest threshold's FPS
        const lowestThreshold = this.zoomFpsConfig[this.zoomFpsConfig.length - 1];

        return lowestThreshold[1];
    }

    /**
     * Set callback for bus position update FPS (for debug overlay)
     * @param callback - Callback receiving bus position update FPS
     */
    public setOnBusFpsUpdate( callback: ( ( busFps: number ) => void ) | null ): void {
        this.onBusFpsUpdate = callback;
    }

    /**
     * Get current bus position update FPS
     */
    public getBusFps(): number {
        return this.currentBusFps;
    }

    /**
     * Get the currently configured FPS based on the current zoom level.
     * Returns 0 if no throttling is configured.
     * 
     * @returns Configured FPS value for current zoom, or 0 if no throttling
     */
    public getConfiguredFps(): number {
        if ( !this.ready ) {
            return 0;
        }

        const currentZoom = this.map.getZoom();

        return this.getFpsForZoom( currentZoom );
    }

    /**
     * Upload a single vehicle's static data to GPU buffers
     */
    private updateStaticBuffersAtIndex( index: number ) {
        const gl = this.gl;

        // Update charCoordBuffer1
        gl.bindBuffer( gl.ARRAY_BUFFER, this.charCoordBuffer1 );
        gl.bufferSubData(
            gl.ARRAY_BUFFER,
            index * 2 * Float32Array.BYTES_PER_ELEMENT,
            this.charTextureCoords1.subarray( index * 2, index * 2 + 2 ),
        );

        // Update charCoordBuffer2
        gl.bindBuffer( gl.ARRAY_BUFFER, this.charCoordBuffer2 );
        gl.bufferSubData(
            gl.ARRAY_BUFFER,
            index * 2 * Float32Array.BYTES_PER_ELEMENT,
            this.charTextureCoords2.subarray( index * 2, index * 2 + 2 ),
        );

        // Update paintZoneBuffer
        gl.bindBuffer( gl.ARRAY_BUFFER, this.paintZoneBuffer );
        gl.bufferSubData(
            gl.ARRAY_BUFFER,
            index * 4 * Float32Array.BYTES_PER_ELEMENT,
            this.paintZones.subarray( index * 4, index * 4 + 4 ),
        );
    }

    /**
     * Expand buffers when capacity is exceeded
     */
    private expandBuffers() {
        const newSize = this.maxBufferSize * 2;
        const gl = this.gl;

        // Create new larger arrays
        const newInstanceCornerPositions = new Float32Array( newSize * 16 );
        const newCharTextureCoords1 = new Float32Array( newSize * 2 );
        const newCharTextureCoords2 = new Float32Array( newSize * 2 );
        const newPaintZones = new Float32Array( newSize * 4 );
        const newVehicleTextureKeys = new Array<string>( newSize );

        // Copy existing data
        newInstanceCornerPositions.set( this.instanceCornerPositions );
        newCharTextureCoords1.set( this.charTextureCoords1 );
        newCharTextureCoords2.set( this.charTextureCoords2 );
        newPaintZones.set( this.paintZones );
        for ( let i = 0; i < this.vehicleTextureKeys.length; i++ ) {
            newVehicleTextureKeys[i] = this.vehicleTextureKeys[i];
        }

        // Update instance variables
        this.instanceCornerPositions = newInstanceCornerPositions;
        this.charTextureCoords1 = newCharTextureCoords1;
        this.charTextureCoords2 = newCharTextureCoords2;
        this.paintZones = newPaintZones;
        this.vehicleTextureKeys = newVehicleTextureKeys;
        this.maxBufferSize = newSize;

        // Recreate ALL buffers with new size (this happens rarely)
        gl.bindBuffer( gl.ARRAY_BUFFER, this.instanceCornerPositionBuffer );
        gl.bufferData( gl.ARRAY_BUFFER, this.instanceCornerPositions, gl.DYNAMIC_DRAW );

        gl.bindBuffer( gl.ARRAY_BUFFER, this.charCoordBuffer1 );
        gl.bufferData( gl.ARRAY_BUFFER, this.charTextureCoords1, gl.STATIC_DRAW );

        gl.bindBuffer( gl.ARRAY_BUFFER, this.charCoordBuffer2 );
        gl.bufferData( gl.ARRAY_BUFFER, this.charTextureCoords2, gl.STATIC_DRAW );

        gl.bindBuffer( gl.ARRAY_BUFFER, this.paintZoneBuffer );
        gl.bufferData( gl.ARRAY_BUFFER, this.paintZones, gl.STATIC_DRAW );
    }

    updateVehiclePositions() {
        if ( !this.ready || this.isRefreshing ) {
            return;
        }

        const nowMs = performance.now();
        if ( this.minPositionUpdateIntervalMs > 0 ) {
            if ( nowMs - this.lastPositionUpdateMs < this.minPositionUpdateIntervalMs ) {
                return;
            }
            this.lastPositionUpdateMs = nowMs;
        }

        // Track position update FPS
        this.positionUpdateFrameCount++;
        const elapsed = nowMs - this.lastBusFpsUpdateMs;
        if ( elapsed >= 1000 ) {
            this.currentBusFps = Math.round( ( this.positionUpdateFrameCount / elapsed ) * 1000 );
            this.positionUpdateFrameCount = 0;
            this.lastBusFpsUpdateMs = nowMs;
            this.onBusFpsUpdate?.( this.currentBusFps );
        }

        const gl = this.gl;
        const currentTime = Date.now();
        const currentZoom = this.map.getZoom();

        // console.log( `[updateVehiclePositions] zoom=${currentZoom.toFixed(2)}, vehicles=${this.vehicles.length}` );

        const activeInstances = this.vehicles.length;
        let skippedInvalidPosition = 0;
        let skippedBeforeStart = 0;
        let skippedAfterEnd = 0;
        let skippedEmptySegments = 0;

        const skippedSample: string[] = [];

        this.vehicles.forEach( ( vehicle ) => {
            const index = this.vehicleIndexMap.get( vehicle.id );
            if ( index === undefined ) {
                return; // Vehicle not in index map, skip
            }

            const position = this.getVehiclePositionForRender( vehicle, currentTime );
            if ( position != null && !Number.isNaN( position.longitude ) && !Number.isNaN( position.latitude ) ) {
                // Convert position to MercatorCoordinate
                const mercatorCoord = maplibregl.MercatorCoordinate.fromLngLat( [ position.longitude, position.latitude ] );

                // Adjust angle to correct rotation if necessary
                const angle = ( position.bearing ?? 0 ) * ( Math.PI / 180 ) + Math.PI;
                const cosA = Math.cos( angle );
                const sinA = Math.sin( angle );

                let size = this.primaryWidthDegrees( currentZoom ) / 600; // Icon size (adjust as needed)
                size *= this.getVehicleIconSizeMultiplier( vehicle.type );

                // Calculate corner positions with rotation
                const x: number = mercatorCoord.x;
                const y: number = mercatorCoord.y;

                const corners = [
                    [ x + ( -size * cosA + -size * sinA ), y + ( -size * sinA - -size * cosA ) ],
                    [ x + ( size * cosA + -size * sinA ), y + ( size * sinA - -size * cosA ) ],
                    [ x + ( -size * cosA + size * sinA ), y + ( -size * sinA - size * cosA ) ],
                    [ x + ( size * cosA + size * sinA ), y + ( size * sinA - size * cosA ) ],
                ];

                for ( let j = 0; j < 4; j++ ) {
                    this.instanceCornerPositions[index * 16 + j * 4] = corners[j][0];
                    this.instanceCornerPositions[index * 16 + j * 4 + 1] = corners[j][1];
                    this.instanceCornerPositions[index * 16 + j * 4 + 2] = corners[j][0] - Math.fround( corners[j][0] );
                    this.instanceCornerPositions[index * 16 + j * 4 + 3] = corners[j][1] - Math.fround( corners[j][1] );
                }
            } else {
                // Clear stale data to avoid ghost rendering
                const base = index * 16;
                this.instanceCornerPositions.fill( 0, base, base + 16 );
                skippedInvalidPosition += 1;

                const timeline = this.vehicleManager.getRealtimeCache()?.timelines.get( vehicle.id );
                const timeWindow = timeline !== undefined ? timelineEpochTimeWindowMs( timeline ) : null;

                if ( timeWindow === null ) {
                    skippedEmptySegments += 1;
                    if ( skippedSample.length < VehicleCustomLayer.SKIP_SAMPLE_LIMIT ) {
                        skippedSample.push( vehicle.id );
                    }
                } else {
                    const { start, end } = timeWindow;
                    if ( currentTime < start ) {
                        skippedBeforeStart += 1;
                    } else if ( currentTime > end ) {
                        skippedAfterEnd += 1;
                    }
                    if ( skippedSample.length < VehicleCustomLayer.SKIP_SAMPLE_LIMIT ) {
                        skippedSample.push( `${ vehicle.id }|${ start }-${ end }` );
                    }
                }
            }
        } );

        // Update buffer with new positions
        gl.bindBuffer( gl.ARRAY_BUFFER, this.instanceCornerPositionBuffer );
        gl.bufferSubData(
            gl.ARRAY_BUFFER,
            0,
            this.instanceCornerPositions.subarray( 0, activeInstances * 16 ),
        );

        if ( __IS_DEV__ && skippedInvalidPosition > 0 ) {
            const now = Date.now();
            if ( now - this.lastSkipLogMs > 10000 ) {
                this.lastSkipLogMs = now;
                console.debug( `[VehicleCustomLayer] Skipped ${ skippedInvalidPosition }/${ activeInstances } vehicles due to missing position data.` );
            }

            const ratio = skippedInvalidPosition / Math.max( 1, activeInstances );
            if ( ratio > 0.05 && now - this.lastSkipDetailLogMs > 15000 ) {
                this.lastSkipDetailLogMs = now;
                console.debug( "[VehicleCustomLayer] Skip diagnostic", {
                    "skipped":       skippedInvalidPosition,
                    "active":        activeInstances,
                    "beforeStart":   skippedBeforeStart,
                    "afterEnd":      skippedAfterEnd,
                    "emptySegments": skippedEmptySegments,
                    "sample":        skippedSample,
                } );
            }
        }
    }

    /*
     * Width palette (approx.) observed in CartoCSS
     * for highway=primary, in pixels, according to integer zoom.
     * z < 8 => no primary in standard style sheet
     * (could leave 0 px or not display it).
     */
    primaryWidths = new Map<number, number>( [
    // -- Low zoom / roads-low-zoom --
        [ 8, 1.0 ], // @primary-width-z8 = 1
        [ 9, 1.4 ], // @primary-width-z9 = 1.4
        [ 10, 1.8 ], // @primary-width-z10= 1.8
        [ 11, 1.8 ], // sometimes stable between z=10 and z=11
        // -- Normal zoom / roads-fill --
        [ 12, 3.5 ], // @primary-width-z12= 3.5
        [ 13, 5.0 ], // @primary-width-z13= 5
        [ 14, 5.0 ], // (not explicitly defined in sheet, set to 5)
        [ 15, 10.0 ], // @primary-width-z15= 10
        [ 16, 10.0 ], // (idem)
        [ 17, 18.0 ], // @primary-width-z17= 18
        [ 18, 21.0 ], // @primary-width-z18= 21
        [ 19, 27.0 ], // @primary-width-z19= 27
        [ 20, 33.0 ], // @primary-width-z20= 33
    ] );

    /**
     * Calculates the width in degrees (at the equator) of a highway=primary road
     * based on a decimal zoom, using the standard OSM style sheet.
     *
     * - For z < 12, get "low zoom" widths (#roads-low-zoom).
     * - From z = 12, take "normal" widths (#roads-fill).
     * - Perform linear interpolation between integer levels.
     * - Below z=8, primary road generally doesn't appear => force to 0 px.
     */
    private primaryWidthDegrees( zoom: number ): number {
    /*
     * 1) Min/max bounds handling
     *    Below 8, "primary" road is not supposed to display => 0 px
     */
        if ( zoom < 8 ) {
            return 0;
        }

        // Above 20, cap at 20 (or extrapolate)
        if ( zoom > 20 ) {
            zoom = 20;
        }

        // 2) Zoom floor/ceil
        const zFloor = Math.floor( zoom );
        const zCeil = Math.ceil( zoom );

        // Case where zoom is an integer
        if ( zFloor === zCeil ) {
            const wpx = this.primaryWidths.get( zFloor ) ?? 0;

            return wpx * this.degreesPerPixel( zoom );
        }

        /*
         * 3) Otherwise linear interpolation
         *    Ex: if zoom=10.3 => between wFloor=1.8(px) and wCeil=1.8(px)
         */
        const wFloor = this.primaryWidths.get( zFloor ) ?? 0;
        const wCeil = this.primaryWidths.get( zCeil ) ?? 0;
        const ratio = zoom - zFloor; // ex: 10.3 - 10 = 0.3
        const wpx = wFloor + ( wCeil - wFloor ) * ratio;

        // 4) Conversion px -> degrees
        return wpx * this.degreesPerPixel( zoom );
    }

    /**
     * Returns the number of degrees (at the equator)
     * per pixel for a given decimal zoom (WebMercator projection).
     */
    private degreesPerPixel( z: number ): number {
    // 360° / (256 px * 2^z)
        return 360.0 / ( 256.0 * Math.pow( 2, z ) );
    }

    private drawVehicles( matrix: number[] ) {
        if ( !this.texturesLoaded ) {
            return;
        }
        const gl = this.gl;

        gl.useProgram( this.program );
        gl.enable( gl.BLEND );
        gl.blendFunc( gl.ONE, gl.ONE_MINUS_SRC_ALPHA );

        // Send uniforms to shader
        gl.uniform4fv( gl.getUniformLocation( this.program, "u_eye_high" ), this.eye_high );
        gl.uniform4fv( gl.getUniformLocation( this.program, "u_eye_low" ), this.eye_low );

        // Send transformation matrix
        gl.uniformMatrix4fv( gl.getUniformLocation( this.program, "u_matrix" ), false, new Float32Array( matrix ) );

        gl.activeTexture( gl.TEXTURE0 + this.textureUnitLetters );
        gl.bindTexture( gl.TEXTURE_2D, this.textureLetters );
        gl.uniform1i( gl.getUniformLocation( this.program, "u_textureLetters" ), this.textureUnitLetters );

        /*
         * Configure attributes
         * a_position
         */
        gl.bindBuffer( gl.ARRAY_BUFFER, this.positionBuffer );
        const aPosition = gl.getAttribLocation( this.program, "a_position" );
        gl.enableVertexAttribArray( aPosition );
        gl.vertexAttribPointer( aPosition, 2, gl.FLOAT, false, 0, 0 );

        // a_texCoord
        gl.bindBuffer( gl.ARRAY_BUFFER, this.texCoordBuffer );
        const aTexCoord = gl.getAttribLocation( this.program, "a_texCoord" );
        gl.enableVertexAttribArray( aTexCoord );
        gl.vertexAttribPointer( aTexCoord, 2, gl.FLOAT, false, 0, 0 );

        // a_charCoord1
        gl.bindBuffer( gl.ARRAY_BUFFER, this.charCoordBuffer1 );
        const aCharCoord1 = gl.getAttribLocation( this.program, "a_charCoord1" );
        gl.enableVertexAttribArray( aCharCoord1 );
        gl.vertexAttribPointer( aCharCoord1, 2, gl.FLOAT, false, 0, 0 );
        gl.vertexAttribDivisor( aCharCoord1, 1 );

        // a_charCoord2
        gl.bindBuffer( gl.ARRAY_BUFFER, this.charCoordBuffer2 );
        const aCharCoord2 = gl.getAttribLocation( this.program, "a_charCoord2" );
        gl.enableVertexAttribArray( aCharCoord2 );
        gl.vertexAttribPointer( aCharCoord2, 2, gl.FLOAT, false, 0, 0 );
        gl.vertexAttribDivisor( aCharCoord2, 1 );

        // a_paintZone
        gl.bindBuffer( gl.ARRAY_BUFFER, this.paintZoneBuffer );
        const aPaintZone = gl.getAttribLocation( this.program, "a_paintZone" );
        gl.enableVertexAttribArray( aPaintZone );
        gl.vertexAttribPointer( aPaintZone, 4, gl.FLOAT, false, 0, 0 );
        gl.vertexAttribDivisor( aPaintZone, 1 );

        // a_cornerPosition0, a_cornerPosition1, a_cornerPosition2, a_cornerPosition3
        const aCornerPosition0 = gl.getAttribLocation( this.program, "a_cornerPosition0" );
        const aCornerPosition1 = gl.getAttribLocation( this.program, "a_cornerPosition1" );
        const aCornerPosition2 = gl.getAttribLocation( this.program, "a_cornerPosition2" );
        const aCornerPosition3 = gl.getAttribLocation( this.program, "a_cornerPosition3" );

        gl.bindBuffer( gl.ARRAY_BUFFER, this.instanceCornerPositionBuffer );
        gl.enableVertexAttribArray( aCornerPosition0 );
        gl.enableVertexAttribArray( aCornerPosition1 );
        gl.enableVertexAttribArray( aCornerPosition2 );
        gl.enableVertexAttribArray( aCornerPosition3 );

        const vec4Size = 4 * Float32Array.BYTES_PER_ELEMENT;
        const instanceStride = 16 * Float32Array.BYTES_PER_ELEMENT;

        gl.vertexAttribPointer( aCornerPosition0, 4, gl.FLOAT, false, instanceStride, 0 );
        gl.vertexAttribPointer( aCornerPosition1, 4, gl.FLOAT, false, instanceStride, vec4Size );
        gl.vertexAttribPointer( aCornerPosition2, 4, gl.FLOAT, false, instanceStride, vec4Size * 2 );
        gl.vertexAttribPointer( aCornerPosition3, 4, gl.FLOAT, false, instanceStride, vec4Size * 3 );

        gl.vertexAttribDivisor( aCornerPosition0, 1 );
        gl.vertexAttribDivisor( aCornerPosition1, 1 );
        gl.vertexAttribDivisor( aCornerPosition2, 1 );
        gl.vertexAttribDivisor( aCornerPosition3, 1 );

        const setBatchOffsets = ( batchStart: number ) => {
            const charOffsetBytes = batchStart * 2 * Float32Array.BYTES_PER_ELEMENT;
            gl.bindBuffer( gl.ARRAY_BUFFER, this.charCoordBuffer1 );
            gl.vertexAttribPointer( aCharCoord1, 2, gl.FLOAT, false, 0, charOffsetBytes );

            gl.bindBuffer( gl.ARRAY_BUFFER, this.charCoordBuffer2 );
            gl.vertexAttribPointer( aCharCoord2, 2, gl.FLOAT, false, 0, charOffsetBytes );

            const paintOffsetBytes = batchStart * 4 * Float32Array.BYTES_PER_ELEMENT;
            gl.bindBuffer( gl.ARRAY_BUFFER, this.paintZoneBuffer );
            gl.vertexAttribPointer( aPaintZone, 4, gl.FLOAT, false, 0, paintOffsetBytes );

            const cornerOffsetBytes = batchStart * 16 * Float32Array.BYTES_PER_ELEMENT;
            gl.bindBuffer( gl.ARRAY_BUFFER, this.instanceCornerPositionBuffer );
            gl.vertexAttribPointer( aCornerPosition0, 4, gl.FLOAT, false, instanceStride, cornerOffsetBytes );
            gl.vertexAttribPointer( aCornerPosition1, 4, gl.FLOAT, false, instanceStride, cornerOffsetBytes + vec4Size );
            gl.vertexAttribPointer( aCornerPosition2, 4, gl.FLOAT, false, instanceStride, cornerOffsetBytes + ( vec4Size * 2 ) );
            gl.vertexAttribPointer( aCornerPosition3, 4, gl.FLOAT, false, instanceStride, cornerOffsetBytes + ( vec4Size * 3 ) );
        };

        const vehicleTextureLocation = gl.getUniformLocation( this.program, "u_vehicleTexture" );
        const letterGlyphScaleLocation = gl.getUniformLocation( this.program, "u_letterGlyphScale" );
        const letterPadTopLocation = gl.getUniformLocation( this.program, "u_letterPadTop" );
        const letterPadBottomLocation = gl.getUniformLocation( this.program, "u_letterPadBottom" );
        const letterPadSideLocation = gl.getUniformLocation( this.program, "u_letterPadSide" );
        const letterCharGapLocation = gl.getUniformLocation( this.program, "u_letterCharGap" );
        const letterPreserveAspectLocation = gl.getUniformLocation( this.program, "u_letterPreserveAspect" );

        for ( const batch of this.renderBatches ) {
            if ( batch.count <= 0 ) {
                continue;
            }

            const entry = this.vehicleTextureCache.get( batch.textureKey );
            if ( entry === undefined ) {
                const descriptor = this.textureDescriptorByKey.get( batch.textureKey );
                if ( descriptor !== undefined ) {
                    void this.ensureVehicleTexture( batch.textureKey, descriptor.effectiveType, descriptor.color ).
                        then( () => {
                            animationOrchestrator.requestRepaint();
                        } ).
                        catch( ( error ) => {
                            console.error( "Unable to build missing vehicle texture", error );
                        } );
                }

                continue;
            }
            this.touchVehicleTextureEntry( batch.textureKey );

            gl.activeTexture( gl.TEXTURE0 + this.textureUnitVehicle );
            gl.bindTexture( gl.TEXTURE_2D, entry.texture );
            gl.uniform1i( vehicleTextureLocation, this.textureUnitVehicle );

            const descriptor = this.textureDescriptorByKey.get( batch.textureKey );
            const letterLayout = descriptor === undefined
                ? this.getLetterLabelLayoutForType( "bus" )
                : this.getLetterLabelLayoutForType( descriptor.effectiveType );
            gl.uniform1f( letterGlyphScaleLocation, letterLayout.glyphScale );
            gl.uniform1f( letterPadTopLocation, letterLayout.padTop );
            gl.uniform1f( letterPadBottomLocation, letterLayout.padBottom );
            gl.uniform1f( letterPadSideLocation, letterLayout.padSide );
            gl.uniform1f( letterCharGapLocation, letterLayout.charGap );
            gl.uniform1f( letterPreserveAspectLocation, letterLayout.preserveGlyphAspect ? 1 : 0 );

            setBatchOffsets( batch.start );
            gl.drawArraysInstanced( gl.TRIANGLE_STRIP, 0, 4, batch.count );
        }
    }

}
